import type { TokenStore } from "@/domains/tokens/token-store"

import { MAX_TICK, MIN_TICK } from "../constants"
import { roundToTickSpacing } from "../utils/price-to-tick-conversion"

export enum PriceStrategy {
  STABLE = "stable",
  WIDE = "wide",
  ONE_SIDED_LOWER = "one_sided_lower",
  ONE_SIDED_UPPER = "one_sided_upper",
  FULL_RANGE = "full_range",
}

export interface PriceStrategyConfig {
  key: PriceStrategy
  title: string
  display: string
  description: string
}

export const PRICE_STRATEGY_CONFIGS: Array<PriceStrategyConfig> = [
  {
    key: PriceStrategy.STABLE,
    title: "Stable",
    display: "± 3 ticks",
    description: "Good for stablecoins or low volatility pairs",
  },
  {
    key: PriceStrategy.WIDE,
    title: "Wide",
    display: "–50% to +100%",
    description: "Good for volatile pairs",
  },
  {
    key: PriceStrategy.ONE_SIDED_LOWER,
    title: "One-sided lower",
    display: "–50%",
    description: "Supply liquidity if price goes down",
  },
  {
    key: PriceStrategy.ONE_SIDED_UPPER,
    title: "One-sided upper",
    display: "+100%",
    description: "Supply liquidity if price goes up",
  },
  // {
  //   key: PriceStrategy.FULL_RANGE,
  //   title: "Full range",
  //   display: "0 — ∞",
  //   description: "Temporary description",
  // },
]

export function getPriceStrategyConfigs(): Array<PriceStrategyConfig> {
  return PRICE_STRATEGY_CONFIGS
}

/**
 * Calculate min and max prices based on a price strategy
 * Similar to Uniswap's calculateStrategyPrices function
 */
const LN_1_0001 = Math.log(1.0001)
const WIDE_LOWER_TICK_DELTA = Math.log(0.5) / LN_1_0001 // negative
const WIDE_UPPER_TICK_DELTA = Math.log(2) / LN_1_0001 // positive

function clampTick(tick: number): number {
  return Math.min(MAX_TICK, Math.max(MIN_TICK, tick))
}

function snapTick(tick: number, tickSpacing: number): number {
  return roundToTickSpacing(tick, tickSpacing)
}

function ensureValidOrderedTicks(
  lowerTick: number,
  upperTick: number,
  tickSpacing: number,
): { lowerTick: number; upperTick: number } {
  if (lowerTick < MIN_TICK) {
    lowerTick = MIN_TICK
  }
  if (upperTick > MAX_TICK) {
    upperTick = MAX_TICK
  }

  // Ensure strict ordering (Uniswap requires lower < upper)
  if (lowerTick >= upperTick) {
    const tryUpper = clampTick(lowerTick + tickSpacing)
    if (tryUpper <= MAX_TICK && lowerTick < tryUpper) {
      upperTick = tryUpper
    } else {
      const tryLower = clampTick(upperTick - tickSpacing)
      lowerTick = tryLower
    }
  }

  return { lowerTick, upperTick }
}

function tickToPriceNumberFast(tick: number, token0Decimals: number, token1Decimals: number): number {
  // Pure number math only (no BigNumber) to avoid perf regressions.
  return Math.pow(1.0001, tick) * Math.pow(10, token0Decimals - token1Decimals)
}

export function calculateStrategyTicks({
  strategy,
  currentTick,
  tickSpacing,
}: {
  strategy: PriceStrategy
  currentTick: number
  tickSpacing: number
}): { lowerTick: number; upperTick: number } {
  const activeTick = snapTick(currentTick, tickSpacing)

  let lowerTick: number
  let upperTick: number

  switch (strategy) {
    case PriceStrategy.STABLE: {
      lowerTick = activeTick - 3 * tickSpacing
      upperTick = activeTick + 3 * tickSpacing
      break
    }
    case PriceStrategy.WIDE: {
      lowerTick = activeTick + WIDE_LOWER_TICK_DELTA
      upperTick = activeTick + WIDE_UPPER_TICK_DELTA
      break
    }
    case PriceStrategy.ONE_SIDED_LOWER: {
      lowerTick = activeTick + WIDE_LOWER_TICK_DELTA
      // Force range strictly below current by setting upper bound to previous usable tick
      upperTick = activeTick - tickSpacing
      break
    }
    case PriceStrategy.ONE_SIDED_UPPER: {
      // Force range strictly above current by setting lower bound to next usable tick
      lowerTick = activeTick + tickSpacing
      upperTick = activeTick + WIDE_UPPER_TICK_DELTA
      break
    }
    case PriceStrategy.FULL_RANGE: {
      lowerTick = MIN_TICK
      upperTick = MAX_TICK
      break
    }
    default: {
      lowerTick = activeTick
      upperTick = activeTick + tickSpacing
      break
    }
  }

  const snappedLower = snapTick(clampTick(lowerTick), tickSpacing)
  const snappedUpper = snapTick(clampTick(upperTick), tickSpacing)
  return ensureValidOrderedTicks(snappedLower, snappedUpper, tickSpacing)
}

export function calculateStrategyPrices({
  strategy,
  currentPrice,
  currentTick,
  tickSpacing,
  token0,
  token1,
}: {
  strategy: PriceStrategy
  currentPrice: number
  currentTick: number
  tickSpacing: number
  token0: TokenStore
  token1: TokenStore
}): { minPrice: number; maxPrice: number } {
  // Tick-first: compute ticks (snapped), then derive prices.
  const { lowerTick, upperTick } = calculateStrategyTicks({ strategy, currentTick, tickSpacing })
  const minPrice = tickToPriceNumberFast(lowerTick, token0.decimals, token1.decimals)
  const maxPrice = tickToPriceNumberFast(upperTick, token0.decimals, token1.decimals)

  // Preserve legacy fallback shape if something goes wrong.
  if (!Number.isFinite(minPrice) || !Number.isFinite(maxPrice)) {
    return { minPrice: currentPrice, maxPrice: currentPrice }
  }
  return { minPrice, maxPrice }
}
